import type { CSSProperties } from 'react'

/**
 * İşaretçi kataloğu: tuvale konan simgelerin tek listesi.
 *
 * İki aile var:
 *
 * - **Bizim çizimlerimiz** (`public/markers/wd/*.svg`, 24'lük ızgara): kemik
 *   beyazı dolgu + siyah kontur. FOB'lar tarafın renginde dolguludur.
 * - **Oyunun simgeleri** (`public/markers/wd/team/*.webp`): araçlar, düşman
 *   araçları, paraşüt, nokta. Asılları beyazdır ve takım rengi seçilebilir
 *   (aşağıda `TEAMS`).
 *
 * Eski HLL dosyaları `public/markers/` altında, kataloğdan çıkan Wardogs
 * simgeleri de (`objective`, `enemy`, `rally`, `lz`, `attack`, `defend`,
 * `tank-side`, `heli-side`, `truck-side`, `paratrooper`, sınıflar…) `wd/` altında
 * **duruyor**:
 * kayıtlı slaytlar işaretçiyi dosya yoluyla (`src`) saklıyor, silinirlerse eski
 * planlarda kırık görsel kalırdı. Yalnızca seçiciden çıktılar.
 *
 * `team/littlebird*.webp` da kayıtlı bir slayt için duruyor: o çizim aslında
 * Lakota'dır, katalogda `lakota-top` adıyla geçer. Çizimi değişmediği için eski
 * işaretçi ekranda aynı kalır.
 *
 * Adlar oyunun terimleri, İngilizce kalır, çevrilmez (arama da onlarla).
 */
export type MarkerGroup = 'tactical' | 'vehicles' | 'vehiclesSide' | 'enemy' | 'supply'

const wd = (id: string) => `/markers/wd/${id}.svg`
const team = (id: string) => `/markers/wd/team/${id}.webp`

/** Tek FOB simgesi; tarafı takım rengi söyler. */
const FOB_SRC = team('fob2')

export const MARKERS = [
  { id: 'fob',           group: 'tactical', label: 'FOB',           src: FOB_SRC },
  { id: 'infantry',      group: 'tactical', label: 'Infantry',      src: team('infantry') },
  { id: 'parachute',     group: 'tactical', label: 'Parachute',     src: team('parachute') },
  { id: 'danger',        group: 'tactical', label: 'Danger',        src: wd('danger') },
  { id: 'note',          group: 'tactical', label: 'Note',          src: wd('note') },

  { id: 'v-tank',        group: 'vehicles', label: 'Tank',          src: team('tank') },
  { id: 'v-sph2',        group: 'vehicles', label: 'SPH-2',         src: team('sph2') },
  { id: 'v-apc',         group: 'vehicles', label: 'APC',           src: team('apc') },
  { id: 'v-ural',        group: 'vehicles', label: 'Ural',          src: team('ural') },
  { id: 'v-humvee',      group: 'vehicles', label: 'Humvee',        src: team('humvee') },
  { id: 'v-kodiak',      group: 'vehicles', label: 'Kodiak',        src: team('kodiak') },
  { id: 'v-buggy',       group: 'vehicles', label: 'Buggy',         src: team('buggy') },
  { id: 'v-havoc',       group: 'vehicles', label: 'Havoc',         src: team('havoc') },
  { id: 'v-littlebird',  group: 'vehicles', label: 'Little Bird',   src: team('littlebird-top') },
  { id: 'v-lakota',      group: 'vehicles', label: 'Z20 Lakota',    src: team('lakota-top') },

  { id: 's-tank',        group: 'vehiclesSide', label: 'Tank',        src: team('tank-side') },
  { id: 's-gepard',      group: 'vehiclesSide', label: 'Gepard',      src: team('gepard-side') },
  { id: 's-sph2',        group: 'vehiclesSide', label: 'SPH-2',       src: team('sph2-side') },
  { id: 's-apc',         group: 'vehiclesSide', label: 'APC',         src: team('apc-side') },
  { id: 's-ural',        group: 'vehiclesSide', label: 'Ural',        src: team('ural-side') },
  { id: 's-humvee',      group: 'vehiclesSide', label: 'Humvee',      src: team('humvee-side') },
  { id: 's-kodiak',      group: 'vehiclesSide', label: 'Kodiak',      src: team('kodiak-side') },
  { id: 's-buggy',       group: 'vehiclesSide', label: 'Buggy',       src: team('buggy-side') },
  { id: 's-havoc',       group: 'vehiclesSide', label: 'Havoc',       src: team('havoc-side') },
  { id: 's-littlebird',  group: 'vehiclesSide', label: 'Little Bird', src: team('littlebird-side') },
  { id: 's-lakota',      group: 'vehiclesSide', label: 'Z20 Lakota',  src: team('lakota-side') },

  // Oyunun "spotted" simgeleri: görülen düşman. Araçlarda ad aynı, grup ayrı.
  { id: 'e-enemy',       group: 'enemy', label: 'Enemy',       src: team('enemy') },
  { id: 'e-fob',         group: 'enemy', label: 'FOB',         src: team('spotted-fob') },
  { id: 'e-tank',        group: 'enemy', label: 'Tank',        src: team('spotted-tank') },
  { id: 'e-apc',         group: 'enemy', label: 'APC',         src: team('spotted-apc') },
  { id: 'e-ural',        group: 'enemy', label: 'Ural',        src: team('spotted-ural') },
  { id: 'e-kodiak',      group: 'enemy', label: 'Kodiak',      src: team('spotted-kodiak') },
  { id: 'e-havoc',       group: 'enemy', label: 'Havoc',       src: team('spotted-havoc') },
  { id: 'e-littlebird',  group: 'enemy', label: 'Little Bird', src: team('spotted-littlebird') },

  { id: 'ammo',          group: 'supply',   label: 'Ammo',          src: wd('ammo') },
  { id: 'fuel',          group: 'supply',   label: 'Fuel',          src: wd('fuel') },
  { id: 'mechanical',    group: 'supply',   label: 'Mechanical',    src: wd('tires') },
  { id: 'build',         group: 'supply',   label: 'Build',         src: wd('build') },
] as const satisfies readonly { id: string; group: MarkerGroup; label: string; src: string }[]

export type MarkerDef = (typeof MARKERS)[number]

export const MARKER_GROUPS: MarkerGroup[] = ['tactical', 'vehicles', 'vehiclesSide', 'enemy', 'supply']

/**
 * FOB'lar — konunca **alanıyla** gelir.
 *
 * Alan 120 × 120 m'lik bir kare (merkezden 60 m): ölçü wardogs.tech savaş
 * odasındaki FOB bölgesinden (`fob-zone`, 60 m yarı kenar). Orada kare hep
 * kuzeye bakıyor; bizde döndürülebilir (`PlacedMarker.rotation`, derece),
 * çünkü FOB'un sahadaki yönü planın parçası.
 *
 * Renk tarafın rengidir — simgenin dolgusu, alanın kenarı ve işaretçinin
 * üçgeni aynı renkte. Tonlar savaş odasının takım renkleri: harita üstünde
 * `lib/factions.ts`'teki koyu artwork tonlarından daha iyi okunuyor. Takım
 * seçilmemiş FOB'un alanı beyazdır.
 */
export const FOB_ZONE_M = 120

/** Yaklaşık havan menzili: FOB'un çevresindeki çemberin yarıçapı. */
export const FOB_MORTAR_RADIUS_M = 700

export const TEAMS = [
  { id: 'lonestar',  label: 'Lonestar',  color: '#5fb8ff' },
  { id: 'valkyra',   label: 'Valkyra',   color: '#ff3b3b' },
  { id: 'manticore', label: 'Manticore', color: '#3ddc84' },
] as const

export type TeamId = (typeof TEAMS)[number]['id']

/**
 * Takım rengi **dosya yolundadır**: `tank.webp` beyaz, `tank-valkyra.webp`
 * kırmızı. İşaretçi ve okun başlangıç simgesi kayıtta zaten yalnızca `src`
 * taşıyor, FOB'ların kuralı da bu; yeni alan, WebSocket ya da migration gerekmez.
 *
 * Kopyalar önceden boyanır (`scripts/tint-team-markers.py`): beyaz çarpılınca
 * dolgu renge döner, kontur siyah kalır. Çalışma anında CSS filtresi her
 * yakınlaştırma karesinde yüzlerce simgeyi yeniden boyardı.
 */
const TEAM_SRC = /^(\/markers\/wd\/team\/.+?)(?:-(lonestar|valkyra|manticore))?\.webp$/

/** Simgeye takım rengi seçilebilir mi. */
export function isTeamMarker(src: string): boolean {
  return TEAM_SRC.test(src)
}

/** Simgenin takımı; beyaz ya da takımsız simgede `null`. */
export function teamOf(src: string): TeamId | null {
  return (src.match(TEAM_SRC)?.[2] as TeamId | undefined) ?? null
}

/** Aynı simgenin istenen takımdaki yolu (`null` = beyaz). Takımsız simge olduğu gibi döner. */
export function teamSrc(src: string, teamId: TeamId | null): string {
  const m = src.match(TEAM_SRC)
  if (!m) return src
  return teamId ? `${m[1]}-${teamId}.webp` : `${m[1]}.webp`
}

/**
 * Eskiden her tarafın ayrı FOB dosyası vardı. Yüklenen işaretçi tek simgeye ve
 * o tarafın rengine çevrilir; alan, açı ve atamalar olduğu gibi kalır.
 *
 * `team/fob*.webp` simgenin bulanık ilk hâlidir. Yeni çizim aynı ada konsaydı
 * tarayıcı önbelleğinden eskisi görünmeye devam ederdi, o yüzden adı `fob2`.
 */
const LEGACY_FOBS: Record<string, TeamId> = {
  [wd('fob-lonestar')]: 'lonestar',
  [wd('fob-valkyra')]: 'valkyra',
  [wd('fob-manticore')]: 'manticore',
}

export function migrateMarkerSrc(src: string): string {
  if (!src) return src // kayıt elden geçmemiş olabilir, yükleme burada patlamasın
  const legacy = LEGACY_FOBS[src]
  if (legacy) return teamSrc(FOB_SRC, legacy)
  return teamSrc(src, null) === team('fob') ? teamSrc(FOB_SRC, teamOf(src)) : src
}

/**
 * İşaretçi bir FOB mu (alanıyla gelen). Düşman grubundaki `spotted-fob` değil:
 * o görülen bir yapının işaretidir, alan çizmez.
 */
export function fobOf(src: string): { color: string } | null {
  const s = migrateMarkerSrc(src)
  if (teamSrc(s, null) !== FOB_SRC) return null
  const t = teamOf(s)
  return { color: TEAMS.find(x => x.id === t)?.color ?? '#ffffff' }
}

/**
 * Yan görünümler enli: kareye sığdırılınca 24 piksellik işaretçide 9 piksel
 * yüksekliğe iniyorlardı. Kutudan iki yana taşarlar ve üçgene oturmak için dibe
 * hizalanırlar.
 */
const SIDE_SRC = /-side(?:-(?:lonestar|valkyra|manticore))?\.webp$/

export function isSideMarker(src: string): boolean {
  return SIDE_SRC.test(src)
}

const ICON_SHADOW = 'drop-shadow(0 0 3px #000) drop-shadow(1px 2px 5px #000)'

export function markerIconStyle(src: string, size: number): CSSProperties {
  // FOB'un kendi siyah kenarı var; üstüne gölge binince kenar bulanıklaşıyordu.
  if (fobOf(src)) return { width: size, height: size, objectFit: 'contain' }
  if (!isSideMarker(src)) return { width: size, height: size, objectFit: 'contain', filter: ICON_SHADOW }
  const width = size * 1.5
  return {
    width, height: size, marginLeft: (size - width) / 2, maxWidth: 'none',
    objectFit: 'contain', objectPosition: 'center bottom', filter: ICON_SHADOW,
  }
}

/**
 * Üçgensiz simgeler: FOB ve Enemy tam tıklanan noktaya, ortasıyla oturur. Başlık,
 * metin ve atamalar diğer işaretçilerdeki gibi çalışır.
 */
export function isPinlessMarker(src: string): boolean {
  return !!fobOf(src) || teamSrc(src, null) === team('enemy')
}

/** Simge kutusunun kenarı, piksel. FOB bir bölgenin merkezi olduğu için daha büyük. */
export function markerIconSize(src: string): number {
  return fobOf(src) ? 34 : 24
}

/** İşaretçinin üçgeni simgeyle aynı renkte gelir. */
export function markerDefaultColor(src: string): string {
  const t = teamOf(src)
  return fobOf(src)?.color ?? TEAMS.find(x => x.id === t)?.color ?? '#ffffff'
}
